import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';

import { timeEntryService, userService, settingsService } from '../../services/api';
import { formatHoursMinutes, formatMinutes } from '../../utils/formatHours';

/* ---------- date helpers ---------- */
const pad = (n) => String(n).padStart(2, '0');
const toDateString = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthBounds = (year, month) => {
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return { start: toDateString(start), end: toDateString(end) };
};
// Week-start for a 'YYYY-MM-DD' date, honoring the configured start day
// ('monday' by default, 'sunday' when the admin configures it).
const weekStartOf = (dateStr, startDay = 'monday') => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // 0 = Sunday .. 6 = Saturday
  const diff = startDay === 'sunday' ? dow : (dow + 6) % 7;
  dt.setDate(dt.getDate() - diff);
  return toDateString(dt);
};
const dayIndexOf = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return (new Date(y, m - 1, d).getDay() + 6) % 7; // 0 = Monday
};
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/* ---------- styled ---------- */
const Page = styled.div`max-width: 960px;`;
const Card = styled.div`
  background: #fff; border-radius: 0.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 1.5rem; margin-bottom: 1.5rem;
`;
const Title = styled.h1`font-size: 1.5rem; color: var(--ve-ink-800); margin: 0 0 1rem;`;
const Controls = styled.div`display: flex; gap: 1rem; flex-wrap: wrap; align-items: flex-end;`;
const Field = styled.div`display: flex; flex-direction: column; min-width: 220px;`;
const Label = styled.label`font-size: 0.8rem; font-weight: 600; color: var(--ve-ink-600); margin-bottom: 0.35rem;`;
const Select = styled.select`
  padding: 0.55rem 0.75rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.95rem; background: #fff;
  &:focus { outline: none; border-color: var(--act-primary); }

  @media (max-width: 576px) {
    font-size: 1rem; /* >=16px prevents iOS zoom */
    min-height: 44px;
  }
`;
const MonthNav = styled.div`display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;`;
const NavBtn = styled.button`
  display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-strong); border-radius: 0.25rem; background: #fff; color: var(--ve-ink-800); font-weight: 600; cursor: pointer;
  & svg { color: var(--ve-ink-800); }
  &:hover { background: var(--ve-blue-050); border-color: var(--act-primary); color: var(--act-primary); }

  @media (max-width: 576px) {
    min-height: 44px; min-width: 44px; justify-content: center;
  }
`;
const MonthLabel = styled.span`font-weight: 600; color: var(--ve-ink-800); min-width: 140px; text-align: center;`;
const Hint = styled.p`font-size: 0.8rem; color: var(--ve-ink-500); margin: 1rem 0 0.5rem;`;
const WeekBlock = styled.div`border: 1px solid #eef1f3; border-radius: 0.4rem; margin-bottom: 1rem; overflow: hidden;`;
const WeekHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 0.6rem 1rem;
  background: var(--surface-subtle); font-weight: 600; color: var(--ve-ink-800); font-size: 0.9rem;

  @media (max-width: 576px) {
    flex-wrap: wrap; gap: 0.5rem; padding: 0.6rem 0.75rem;
  }
`;
const Row = styled.div`
  display: grid; grid-template-columns: 2.2rem 1.6fr 1.4fr 0.8fr 1fr; align-items: center; gap: 0.5rem;
  padding: 0.55rem 1rem; border-top: 1px solid #eef1f3;
  background: ${({ $status }) => ($status === 'challenged' ? '#fdecec' : $status === 'approved' ? '#f6f9f7' : '#fff')};

  /* Stacked card layout on phones: checkbox left, day+project stacked, hours+status right */
  @media (max-width: 576px) {
    grid-template-columns: auto minmax(0, 1fr) auto;
    grid-template-areas:
      'check day hours'
      'check project status';
    column-gap: 0.6rem; row-gap: 0.2rem;
    padding: 0.5rem 0.75rem;

    .cell-check { grid-area: check; align-self: center; }
    .cell-day { grid-area: day; }
    .cell-project { grid-area: project; font-size: 0.85rem; color: var(--ve-ink-600); overflow-wrap: anywhere; }
    .cell-hours { grid-area: hours; justify-self: end; font-weight: 600; }
    .cell-status { grid-area: status; justify-self: end; }
  }
`;
const HeaderRow = styled(Row)`
  background: #fbfcfd; font-size: 0.75rem; color: var(--ve-ink-500); text-transform: uppercase; letter-spacing: 0.03em; padding-top: 0.4rem; padding-bottom: 0.4rem;

  @media (max-width: 576px) { display: none; }
`;
// Horizontal scroll wrapper so the fixed 5-col grid stays usable on narrow screens.
const RowsScroll = styled.div`overflow-x: auto;`;
const RowsInner = styled.div`
  min-width: 640px;

  @media (max-width: 576px) { min-width: 0; }
`;
// Padded label around the selection checkbox for a comfortable (~44px) tap target.
const CheckLabel = styled.label`
  display: inline-flex; align-items: center; justify-content: flex-start; cursor: pointer;

  input { cursor: pointer; margin: 0; }
  input:disabled { cursor: not-allowed; }

  @media (max-width: 576px) {
    min-width: 44px; min-height: 44px; justify-content: center;

    input { transform: scale(1.4); }
  }
`;
const Badge = styled.span`
  display: inline-block; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600;
  color: #fff; background: ${({ $status }) => ($status === 'approved' ? 'var(--act-approve)' : $status === 'challenged' ? 'var(--act-danger)' : '#f39c12')};
`;
const WeekApprove = styled.button`
  padding: 0.35rem 0.75rem; border: 1px solid var(--act-approve); color: var(--act-approve); background: #fff; border-radius: 0.25rem;
  font-size: 0.8rem; cursor: pointer; &:hover { background: var(--act-approve); color: #fff; } &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 576px) {
    min-height: 44px; padding: 0.5rem 0.9rem;
  }
`;
const ActionBar = styled.div`
  position: sticky; bottom: 0; display: flex; gap: 0.75rem; align-items: center; flex-wrap: wrap;
  padding: 1rem; background: #fff; border-top: 1px solid #eef1f3; margin-top: 1rem;

  /* Phone layout: row 1 = selected count + primary approve; secondary actions wrap below */
  @media (max-width: 576px) {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; padding: 0.75rem;

    .act-spacer { display: none; }
    .sel-count { order: 1; align-self: center; }
    .act-primary { order: 2; min-height: 48px; }
    .act-challenge { order: 3; }
    .act-select { order: 4; }
    .act-clear { order: 5; }
    .act-month { order: 6; }
    .act-challenge, .act-select, .act-clear, .act-month {
      font-size: 0.85rem; padding: 0.5rem 0.6rem;
    }
  }
`;
const Btn = styled.button`
  padding: 0.55rem 1.1rem; border-radius: 0.25rem; font-size: 0.9rem; font-weight: 600; cursor: pointer; border: none;
  color: #fff; background: ${({ $variant }) => ($variant === 'challenge' ? 'var(--act-danger)' : $variant === 'ghost' ? 'var(--ve-ink-600)' : 'var(--act-approve)')};
  &:hover { filter: brightness(0.94); } &:disabled { opacity: 0.45; cursor: not-allowed; }

  @media (max-width: 576px) {
    min-height: 44px;
  }
`;
// Small per-row action buttons (Godkend / Gennemgå — REQ #16/#17)
const RowBtn = styled.button`
  padding: 0.3rem 0.6rem; border-radius: 0.25rem; font-size: 0.78rem; font-weight: 600; cursor: pointer;
  border: 1px solid ${({ $approve }) => ($approve ? 'var(--act-approve)' : 'var(--act-primary)')};
  color: ${({ $approve }) => ($approve ? 'var(--act-approve)' : 'var(--act-primary)')}; background: #fff;
  &:hover { background: ${({ $approve }) => ($approve ? 'var(--act-approve)' : 'var(--act-primary)')}; color: #fff; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }

  @media (max-width: 576px) { min-height: 40px; }
`;
// Detail rows in the Gennemgå (review) modal
const SegList = styled.div`margin: 0.5rem 0 0.75rem;`;
const SegRow = styled.div`display: flex; justify-content: space-between; padding: 0.35rem 0; border-bottom: 1px solid var(--surface-subtle); font-size: 0.9rem; color: var(--ve-ink-800);`;
const SegTotal = styled.div`display: flex; justify-content: space-between; font-weight: 700; color: var(--ve-ink-800); padding: 0.5rem 0 0; font-size: 0.95rem;`;
const HoursInput = styled.input`
  width: 120px; padding: 0.5rem 0.6rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.95rem;
  &:focus { outline: none; border-color: var(--act-primary); }
  @media (max-width: 576px) { font-size: 1rem; min-height: 44px; }
`;
// The contest field stays DECIMAL hours (challengeTimeEntry takes decimal hours),
// so the H:MM reading is echoed beside it — otherwise the customer would type
// 7.5 next to a total that reads 7:30.
const HoursEquiv = styled.span`
  margin-left: 0.6rem; color: var(--ve-ink-600); font-size: 0.95rem; font-variant-numeric: tabular-nums;
`;
const Alert = styled.div`
  padding: 0.75rem 1rem; border-radius: 0.25rem; margin-bottom: 1rem; font-size: 0.9rem;
  background: ${({ $type }) => ($type === 'error' ? 'var(--status-danger-plate)' : '#eafaf1')};
  color: ${({ $type }) => ($type === 'error' ? 'var(--act-danger)' : '#1e8449')};
  border: 1px solid ${({ $type }) => ($type === 'error' ? '#f5c6cb' : '#abebc6')};
`;
const Empty = styled.div`padding: 2rem; text-align: center; color: var(--ve-ink-500);`;
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000;

  @media (max-width: 576px) { align-items: flex-end; }
`;
const ModalBox = styled.div`
  background: #fff; border-radius: 0.5rem; padding: 1.5rem; width: 440px; max-width: 92vw;

  /* Bottom sheet on phones */
  @media (max-width: 576px) {
    width: 100%; max-width: none; border-radius: 0.75rem 0.75rem 0 0;
    padding: 1.25rem 1rem calc(1.25rem + env(safe-area-inset-bottom, 0px));
  }
`;
const TextArea = styled.textarea`
  width: 100%; min-height: 90px; padding: 0.6rem; border: 1px solid var(--border-subtle); border-radius: 0.25rem; font-size: 0.95rem;
  &:focus { outline: none; border-color: var(--act-primary); }

  @media (max-width: 576px) {
    font-size: 1rem; /* >=16px prevents iOS zoom */
  }
`;
const ModalActions = styled.div`
  display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1rem;

  @media (max-width: 576px) {
    flex-direction: column-reverse;

    button { width: 100%; min-height: 48px; }
  }
`;

/* ---------- component ---------- */
const Approvals = () => {
  const { t, i18n } = useTranslation();
  const now = new Date();

  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [workWeekStart, setWorkWeekStart] = useState('monday');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entries, setEntries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeReason, setChallengeReason] = useState('');
  // Per-row review (Gennemgå) modal state (REQ #17/#18)
  const [reviewEntry, setReviewEntry] = useState(null);
  const [reviewMode, setReviewMode] = useState('view'); // 'view' | 'challenge'
  const [chHours, setChHours] = useState('');
  const [chNote, setChNote] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [res, cfg] = await Promise.all([
          userService.getCustomerEmployees({ limit: 1000, page: 1 }),
          settingsService.getAppConfig().catch(() => ({ workWeekStart: 'monday' })),
        ]);
        if (!active) return;
        setWorkWeekStart(cfg && cfg.workWeekStart === 'sunday' ? 'sunday' : 'monday');
        const list = (res && res.items) || [];
        setEmployees(list);
        if (list.length > 0) setEmployeeId(String(list[0].id));
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const loadEntries = useCallback(async () => {
    if (!employeeId) { setEntries([]); return; }
    try {
      setError(null);
      const { start, end } = monthBounds(year, month);
      const res = await timeEntryService.getCustomerTimeEntries({
        employeeId, startDate: start, endDate: end, limit: 200,
      });
      // A pending approval or submitted week does not finish an individual day.
      // The server enforces this too; keep drafts out of every row, total and action.
      const items = ((res && res.items) || []).filter((entry) => Boolean(entry.finishedAt));
      items.sort((a, b) => a.date.localeCompare(b.date));
      setEntries(items);
      setSelected(new Set());
      setReviewEntry(null);
      setChallengeOpen(false);
    } catch (err) {
      setError(err.message);
    }
  }, [employeeId, year, month]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Group entries into weeks keyed by the configured week-start date.
  const weeks = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const wk = weekStartOf(e.date, workWeekStart);
      if (!map.has(wk)) map.set(wk, []);
      map.get(wk).push(e);
    });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([weekStart, list]) => ({
        weekStart,
        entries: list.sort((a, b) => a.date.localeCompare(b.date)),
        totalMinutes: list.reduce((s, x) => s + (x.durationMinutes || 0), 0),
        hasPending: list.some((x) => x.status === 'pending'),
      }));
  }, [entries, workWeekStart]);

  const selectableIds = useMemo(
    () => entries.filter((e) => e.finishedAt && e.status !== 'approved').map((e) => e.id),
    [entries]
  );
  const selectedIds = selectableIds.filter((id) => selected.has(id));

  const toggle = (id) => {
    if (!selectableIds.includes(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSuccess(null);
  };

  const selectAll = () => setSelected(new Set(selectableIds));
  const clearSel = () => setSelected(new Set());

  const runAction = async (fn, resultKey) => {
    try {
      setBusy(true); setError(null); setSuccess(null);
      const res = await fn();
      await loadEntries();
      setSuccess(t(resultKey, { count: res.count != null ? res.count : 0 }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const approveSelected = () =>
    selectedIds.length > 0 && runAction(() => timeEntryService.bulkApprove(selectedIds), 'customer.approvals.resultApproved');
  const approveWeek = (weekStart) =>
    weeks.some((week) => week.weekStart === weekStart && week.hasPending)
      && runAction(() => timeEntryService.approveWeek(Number(employeeId), weekStart), 'customer.approvals.resultApproved');
  // "Godkend alle timer": approve every not-yet-approved day currently shown (REQ #16).
  const approveAll = () =>
    selectableIds.length > 0 && runAction(() => timeEntryService.bulkApprove(selectableIds), 'customer.approvals.resultApproved');
  // One-by-one approve straight from a row (REQ #16).
  const approveOne = (id) =>
    selectableIds.includes(id) && runAction(() => timeEntryService.approveTimeEntry(id), 'customer.approvals.resultApproved');

  // Gennemgå (review) modal (REQ #17): open showing start/stop times + total.
  const openReview = (e) => {
    if (!e.finishedAt) return;
    setReviewEntry(e);
    setReviewMode('view');
    // Decimal on purpose: this prefills the number input the API expects.
    // The H:MM equivalent is echoed next to the field (see HoursEquiv).
    setChHours(((e.durationMinutes || 0) / 60).toFixed(2));
    setChNote('');
    setError(null);
  };
  const closeReview = () => { setReviewEntry(null); setReviewMode('view'); };
  const approveReviewed = async () => {
    if (!reviewEntry?.finishedAt) return;
    const id = reviewEntry.id;
    closeReview();
    await runAction(() => timeEntryService.approveTimeEntry(id), 'customer.approvals.resultApproved');
  };
  // Bestrid from the review modal: the customer states the hours they DO approve
  // (0–24, may exceed logged) plus a note (REQ #18).
  const submitReviewChallenge = async () => {
    if (!reviewEntry?.finishedAt) return;
    const h = Number(chHours);
    if (!chNote.trim()) { setError(t('customer.approvals.challengeReasonRequired')); return; }
    if (!Number.isFinite(h) || h < 0 || h > 24) {
      setError(t('customer.approvals.hoursRange', { defaultValue: 'Approved hours must be between 0 and 24' }));
      return;
    }
    const id = reviewEntry.id;
    closeReview();
    await runAction(() => timeEntryService.challengeTimeEntry(id, chNote.trim(), h), 'customer.approvals.resultChallenged');
  };

  const submitChallenge = async () => {
    if (selectedIds.length === 0) return;
    if (!challengeReason.trim()) { setError(t('customer.approvals.challengeReasonRequired')); return; }
    setChallengeOpen(false);
    await runAction(
      () => timeEntryService.bulkChallenge(selectedIds, challengeReason.trim()),
      'customer.approvals.resultChallenged'
    );
    setChallengeReason('');
  };

  const shiftMonth = (delta) => {
    let m = month + delta; let y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y); setSuccess(null);
  };
  const thisMonth = () => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); setSuccess(null); };

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const monthTotalMinutes = entries.reduce((s, e) => s + (e.durationMinutes || 0), 0);

  if (loading) return <Empty>{t('common.loading')}</Empty>;

  return (
    <Page>
      <Title>{t('customer.approvals.title')}</Title>
      {error && <Alert $type="error">{error}</Alert>}
      {success && <Alert $type="success">{success}</Alert>}

      <Card>
        <Controls>
          <Field>
            <Label>{t('customer.approvals.employee')}</Label>
            <Select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setSuccess(null); }}>
              <option value="">{t('customer.approvals.selectEmployee')}</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name || `${emp.firstName} ${emp.lastName}`}</option>
              ))}
            </Select>
          </Field>
          <Field style={{ minWidth: 'auto' }}>
            <Label>{t('customer.approvals.period')}</Label>
            <MonthNav>
              <NavBtn onClick={() => shiftMonth(-1)}><FiChevronLeft /></NavBtn>
              <MonthLabel>{monthLabel}</MonthLabel>
              <NavBtn onClick={() => shiftMonth(1)}><FiChevronRight /></NavBtn>
              <NavBtn onClick={thisMonth}>{t('customer.approvals.thisMonth')}</NavBtn>
            </MonthNav>
          </Field>
        </Controls>

        {!employeeId ? (
          <Empty>{t('customer.approvals.selectEmployee')}</Empty>
        ) : entries.length === 0 ? (
          <Empty>{t('customer.approvals.noEntries')}</Empty>
        ) : (
          <>
            <Hint>{t('customer.approvals.hint')}</Hint>

            {weeks.map((wk) => (
              <WeekBlock key={wk.weekStart}>
                <WeekHeader>
                  <span>{t('customer.approvals.weekOf', { date: wk.weekStart })}</span>
                  <span style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <span>{t('customer.approvals.weekTotal')}: {formatMinutes(wk.totalMinutes, '0:00')}</span>
                    <WeekApprove disabled={busy || !wk.hasPending} onClick={() => approveWeek(wk.weekStart)}>
                      {t('customer.approvals.approveWeek')}
                    </WeekApprove>
                  </span>
                </WeekHeader>
                <RowsScroll><RowsInner>
                <HeaderRow as="div">
                  <span />
                  <span>{t('customer.approvals.day')}</span>
                  <span>{t('customer.approvals.actions', { defaultValue: 'Actions' })}</span>
                  <span>{t('customer.approvals.hours')}</span>
                  <span>{t('customer.approvals.status')}</span>
                </HeaderRow>
                {wk.entries.map((e) => (
                  <Row key={e.id} $status={e.status}>
                    <CheckLabel className="cell-check">
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        disabled={e.status === 'approved'}
                        onChange={() => toggle(e.id)}
                        aria-label={e.date}
                      />
                    </CheckLabel>
                    <span className="cell-day">
                      <strong>{t(`days.${DAY_KEYS[dayIndexOf(e.date)]}`)}</strong>
                      <br /><small style={{ color: 'var(--ve-ink-500)' }}>{e.date}</small>
                    </span>
                    <span className="cell-project" style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap' }}>
                      {e.status !== 'approved' && (
                        <RowBtn $approve onClick={() => approveOne(e.id)} disabled={busy}>
                          {t('customer.approvals.approve', { defaultValue: 'Godkend' })}
                        </RowBtn>
                      )}
                      <RowBtn onClick={() => openReview(e)} disabled={busy}>
                        {t('customer.approvals.review', { defaultValue: 'Gennemgå' })}
                      </RowBtn>
                    </span>
                    <span className="cell-hours" style={{ fontVariantNumeric: 'tabular-nums' }}>{e.durationFormatted}</span>
                    <span className="cell-status"><Badge $status={e.status}>{t(`status.${e.status}`)}</Badge></span>
                  </Row>
                ))}
                </RowsInner></RowsScroll>
              </WeekBlock>
            ))}

            <ActionBar>
              <span className="sel-count" style={{ color: 'var(--ve-ink-600)', fontSize: '0.85rem' }}>
                {t('customer.approvals.selectedCount', { count: selectedIds.length })}
              </span>
              <Btn className="act-select" $variant="ghost" onClick={selectAll} disabled={busy}>{t('customer.approvals.selectAll')}</Btn>
              <Btn className="act-clear" $variant="ghost" onClick={clearSel} disabled={busy || selectedIds.length === 0}>{t('customer.approvals.clear')}</Btn>
              <div className="act-spacer" style={{ flex: 1 }} />
              <Btn className="act-primary" onClick={approveSelected} disabled={busy || selectedIds.length === 0}>
                <FiCheck style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('customer.approvals.approveSelected')}
              </Btn>
              <Btn className="act-challenge" $variant="challenge" onClick={() => { setChallengeReason(''); setError(null); setChallengeOpen(true); }} disabled={busy || selectedIds.length === 0}>
                <FiX style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('customer.approvals.challengeSelected')}
              </Btn>
              <Btn className="act-month" onClick={approveAll} disabled={busy || selectableIds.length === 0}>{t('customer.approvals.approveAll', { defaultValue: 'Godkend alle timer' })}</Btn>
            </ActionBar>
            <div style={{ textAlign: 'right', color: 'var(--ve-ink-600)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {t('customer.approvals.monthTotal')}: {formatMinutes(monthTotalMinutes, '0:00')}
            </div>
          </>
        )}
      </Card>

      {challengeOpen && (
        <Overlay onClick={() => setChallengeOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{t('customer.approvals.challengeSelected')}</h3>
            <Label>{t('customer.approvals.challengeReason')}</Label>
            <TextArea value={challengeReason} onChange={(e) => setChallengeReason(e.target.value)} />
            <ModalActions>
              <Btn $variant="ghost" onClick={() => setChallengeOpen(false)}>{t('common.cancel')}</Btn>
              <Btn $variant="challenge" onClick={submitChallenge}>{t('customer.approvals.challengeSelected')}</Btn>
            </ModalActions>
          </ModalBox>
        </Overlay>
      )}

      {reviewEntry && (
        <Overlay onClick={closeReview}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>
              {t('customer.approvals.reviewTitle', { defaultValue: 'Review day' })} — {reviewEntry.date}
            </h3>
            <div style={{ color: 'var(--ve-ink-600)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              {reviewEntry.employeeName}
            </div>

            {/* Start/stop breakdown (REQ #17) */}
            {reviewEntry.isDayOff ? (
              <SegList><SegRow><span>{t('customer.approvals.freeDay', { defaultValue: 'Free (off duty)' })}</span><span>0:00</span></SegRow></SegList>
            ) : reviewEntry.segments && (reviewEntry.segments.work || reviewEntry.segments.breaks) ? (
              <SegList>
                {(reviewEntry.segments.work || []).map((w, i) => (
                  <SegRow key={`w${i}`}>
                    <span>{t('customer.approvals.workPeriod', { defaultValue: 'Work' })} {i + 1}</span>
                    <span>{w.start || '—'} → {w.end || '—'}</span>
                  </SegRow>
                ))}
                {(reviewEntry.segments.breaks || []).map((b, i) => (
                  <SegRow key={`b${i}`} style={{ color: 'var(--ve-ink-500)' }}>
                    <span>{t('customer.approvals.breakPeriod', { defaultValue: 'Break' })} {i + 1}</span>
                    <span>{b.start || '—'} → {b.end || '—'}</span>
                  </SegRow>
                ))}
                <SegTotal>
                  <span>{t('customer.approvals.total', { defaultValue: 'Total' })}</span>
                  <span>{reviewEntry.durationFormatted}</span>
                </SegTotal>
              </SegList>
            ) : (
              <SegList>
                <SegTotal>
                  <span>{t('customer.approvals.total', { defaultValue: 'Total' })}</span>
                  <span>{reviewEntry.durationFormatted}</span>
                </SegTotal>
              </SegList>
            )}

            {reviewMode === 'view' ? (
              <ModalActions>
                <Btn $variant="challenge" onClick={() => { setReviewMode('challenge'); setError(null); }}>
                  {t('customer.approvals.contest', { defaultValue: 'Bestrid' })}
                </Btn>
                <Btn onClick={approveReviewed} disabled={busy}>
                  {t('customer.approvals.approve', { defaultValue: 'Godkend' })}
                </Btn>
              </ModalActions>
            ) : (
              <>
                <Label>{t('customer.approvals.approvedHours', { defaultValue: 'Hours you approve (0–24)' })}</Label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <HoursInput type="number" min="0" max="24" step="0.25" value={chHours}
                    onChange={(e) => setChHours(e.target.value)} />
                  <HoursEquiv>= {formatHoursMinutes(chHours)}</HoursEquiv>
                </div>
                <Label style={{ marginTop: '0.75rem' }}>{t('customer.approvals.challengeReason')}</Label>
                <TextArea value={chNote} onChange={(e) => setChNote(e.target.value)}
                  placeholder={t('customer.approvals.contestNotePh', { defaultValue: 'Explain what you are approving and why…' })} />
                <ModalActions>
                  <Btn $variant="ghost" onClick={() => setReviewMode('view')}>{t('common.back', { defaultValue: 'Back' })}</Btn>
                  <Btn $variant="challenge" onClick={submitReviewChallenge} disabled={busy}>
                    {t('customer.approvals.submitContest', { defaultValue: 'Submit challenge' })}
                  </Btn>
                </ModalActions>
              </>
            )}
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
};

export default Approvals;
